import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Buttons/Button';
import DataTable from '../../components/DataTable/DataTable';
import { IColInfo } from '../../components/DataTable/Interfaces';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import { useLocalStorage } from '../../components/Hooks/LocalStorageHook/useLocalStorage';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import SearchCriteria from '../../models/common/SearchCriteria';
import UserDefinedFieldModel from '../../models/UserDefinedFieldModel';
import DataService from "../../services/data.service";

const UserDefinedFields: React.FC = () => {
    const [data, setData] = useState<UserDefinedFieldModel[]>();
    const { handleResult, handleException } = useNotifications();
    const { isAuthorizedFor } = useAuth();
    const navigate = useNavigate();
    const [formData, setFormData] = useLocalStorage<SearchCriteria>("udfFilter", { quickSearch: '' })

    useEffect(() => {
        getTableData(formData);
    }, []);

    function search() {
        if (formData.quickSearch.length >= 3)
            getTableData(formData)
    }

    function setDefaultCriteria() {
        setFormData({ quickSearch: '' })
        getTableData({ quickSearch: '' })
    }

    function getTableData(criteria) {
        localStorage.setItem('udfFilter', JSON.stringify(criteria));
        DataService.post<UserDefinedFieldModel[]>('api/v1/userdefinedfields/search', criteria)
            .then(response => {
                setData(response);
            })
            .catch(e => { handleException(e); });
    }

    function add() {
        navigate('/plm/itemsetup/userdefinedfield/add', { state: { title: 'New user defined field', fromMenu: false } });
    }

    function deleteRow(rowData) {
        DataService.delete('api/v1/userdefinedfields/', rowData.id)
            .then(response => {
                handleResult(response);
                getTableData(formData);
            })
            .catch(e => { handleException(e); });
    }

    const formConfig: FormConfig = {
        fields: [{ name: 'quickSearch', label: 'Search' }],
        handleFieldChanged: search,
        handleSubmit: () => getTableData(formData),
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    const colInfos: IColInfo[] = [
        { title: 'ID', key: true, dataProp: 'id', dataType: 'number' },
        { title: 'Type', dataProp: 'userDefinedFieldType', sort: 'asc', filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Name', dataProp: 'name', sort: 'asc', filterInfo: { filterType: 'Textbox' } },
        { title: 'Category', dataProp: 'userDefinedFieldCategory', filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Item types', dataProp: 'itemTypes', filterInfo: { filterType: 'Textbox' } },
        { title: 'Project types', dataProp: 'projectTypes', filterInfo: { filterType: 'Textbox' } },
    ];
    if (isAuthorizedFor(ApplicationFunctions.EditUdf)) {
        colInfos.push(
            {
                headerButtons: [{ text: "Add", icon: 'fa-plus-square', color: '#26A69A', onClick: add }],
                rowButtons: [{ text: "Delete", icon: 'fa-trash', color: '#F3565D', onClick: deleteRow }],
            });
    }

    return (
        <div className='main-content'>
            <Form title='User-defined fields' footer={(
                <ButtonBar offset={2}>
                    <Button text='Search' {...getSubmitButtonProps()} />
                    <Button text='Clear' type='button' onClick={setDefaultCriteria} />
                </ButtonBar>
            )}>
                <Container fluid>
                    <TextboxField {...getFieldProps('quickSearch')} />
                </Container>
            </Form>
            <div className="card" style={{ margin: '0 12px', padding: 12 }}>
                <DataTable
                    name='dtUserDefinedFields'
                    tableData={data}
                    colInfos={colInfos}
                    navigateTitle='UDF'
                    navigateUrl='/plm/itemsetup/userdefinedfield/'
                />
            </div>
        </div>
    );
}
export default UserDefinedFields;